import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, CalendarDays, ChevronRight } from "lucide-react";
import { format, isBefore, startOfToday } from "date-fns";
import { Calendar } from "./ui/calendar";
import { Button } from "./ui/button";
import { useBooking } from "../contexts/BookingContext";

/**
 * Global Date Selection Modal
 * 
 * Shown when user clicks on a category (optional)
 * Allows selecting event date or skipping to browse without date
 */
export default function DateSelectionModal() {
  const navigate = useNavigate();
  const { 
    eventDate, 
    setEventDate, 
    showDateModal, 
    closeDateModal, 
    pendingCategoryNav 
  } = useBooking();
  
  const [localDate, setLocalDate] = useState(eventDate);
  
  useEffect(() => {
    if (showDateModal) {
      setLocalDate(eventDate);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [showDateModal, eventDate]);
  
  // Handle escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") handleSkip(); };
    if (showDateModal) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showDateModal]);
  
  const handleContinue = () => {
    if (localDate) {
      setEventDate(localDate);
    }
    closeDateModal();
    if (pendingCategoryNav) {
      navigate(pendingCategoryNav);
    }
  };
  
  const handleSkip = () => {
    closeDateModal();
    if (pendingCategoryNav) {
      navigate(pendingCategoryNav);
    }
  };
  
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleSkip();
    }
  };
  
  if (!showDateModal) return null;
  
  const today = startOfToday();
  // Section 5 Rev 2 — allow today and tomorrow. Same/next-day event dates are
  // pickup-only (enforced on the product page + backend), not disallowed.
  const minDate = today;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
        data-testid="date-modal-backdrop"
      >
        {/* Modal */}
        <div 
          className="bg-white w-full max-w-md shadow-2xl animate-in fade-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
          data-testid="date-selection-modal"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-[#282828]/5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg text-[#282828] font-light">Select Your Event Date</h2>
              <button 
                onClick={handleSkip}
                className="text-[#676664]/40 hover:text-[#676664] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[12px] text-[#676664]/60">
              Choose when you need your outfit to check availability
            </p>
          </div>
          
          {/* Calendar */}
          <div className="px-6 py-4 flex flex-col items-center">
            <Calendar
              mode="single"
              selected={localDate}
              onSelect={setLocalDate}
              disabled={(date) => isBefore(date, minDate)}
              className="mx-auto"
              classNames={{
                day_selected: "bg-[#9A7B4F] text-white hover:bg-[#8a6b3f] focus:bg-[#8a6b3f]",
                day_today: "bg-[#FAF8F0] text-[#282828]",
              }}
            />
            
            {/* Selected date preview */}
            {localDate && (
              <div className="mt-4 p-3 bg-[#FAF8F0] border border-[#282828]/5 w-full" data-testid="date-modal-selected-preview">
                <div className="flex items-center gap-2 text-[12px] text-[#282828]">
                  <CalendarDays className="w-4 h-4 text-[#9A7B4F]" />
                  <span className="font-medium">{format(localDate, "EEEE, d MMMM yyyy")}</span>
                </div>
                {(() => {
                  const diff = Math.floor((localDate - today) / (1000 * 60 * 60 * 24));
                  if (diff <= 1) {
                    return (
                      <p className="text-[11px] text-[#9A7B4F] mt-1 ml-6 font-medium" data-testid="date-modal-pickup-only-hint">
                        Pickup only — same-day and next-day bookings are collected in-store.
                      </p>
                    );
                  }
                  return (
                    <p className="text-[11px] text-[#676664]/60 mt-1 ml-6">
                      Your outfit arrives 1–2 days before this date
                    </p>
                  );
                })()}
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="px-6 pb-6 flex flex-col gap-3">
            <Button
              onClick={handleContinue}
              disabled={!localDate}
              className="w-full bg-[#282828] hover:bg-[#1a1a1a] text-white text-[12px] tracking-[0.08em] uppercase py-3 h-auto"
              data-testid="date-modal-continue"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
            
            <button
              onClick={handleSkip}
              className="text-[12px] text-[#676664]/60 hover:text-[#676664] transition-colors py-2"
              data-testid="date-modal-skip"
            >
              Browse without selecting a date
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
